import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  KEYCODE_MAP,
  KEYCODE_MACRO_MAP,
  Tooltip,
  Iconfont,
} from "@eweadn-qiankun/shared-components";
import { getElementXyData } from "./hooks/useAdvancedKeyState";

/** 与图3一致：热键一 / 热键二 仅允许选择的修饰键（扩展字符） */
/** 用于从 keyCode 显示标签（修改键名不在标准 KEYCODE_MAP 时） */
export const CB_MODIFIER_KC_TO_NAME: Record<number, string> = {
  224: "L-Ctrl",
  228: "R-Ctrl",
  225: "L-Shift",
  229: "R-Shift",
  226: "L-Alt",
  230: "R-Alt",
  227: "L-Win",
};

export const CB_MODIFIER_OPTIONS: {
  labelKey: string;
  keyCode: number;
}[] = [
  { labelKey: "keyboard.advancedKey.cbModifier.lCtrl", keyCode: 224 },
  { labelKey: "keyboard.advancedKey.cbModifier.rCtrl", keyCode: 228 },
  { labelKey: "keyboard.advancedKey.cbModifier.lShift", keyCode: 225 },
  { labelKey: "keyboard.advancedKey.cbModifier.rShift", keyCode: 229 },
  { labelKey: "keyboard.advancedKey.cbModifier.lAlt", keyCode: 226 },
  { labelKey: "keyboard.advancedKey.cbModifier.rAlt", keyCode: 230 },
  { labelKey: "keyboard.advancedKey.cbModifier.lWin", keyCode: 227 },
];

/** 根据槽位与键码生成展示文案（不依赖可能被污染的 label） */
export function getCbTriggerSlotDisplayLabel(
  keyCode: number | string | undefined,
  slotIndex: number
): string {
  const kc = Number(keyCode);
  if (!Number.isFinite(kc) || kc <= 0) return "";
  if (slotIndex <= 1) {
    return (
      CB_MODIFIER_KC_TO_NAME[kc] ||
      KEYCODE_MAP[kc]?.name ||
      KEYCODE_MACRO_MAP[kc]?.name ||
      String(kc)
    );
  }
  return KEYCODE_MAP[kc]?.name || KEYCODE_MACRO_MAP[kc]?.name || String(kc);
}

type CbProps = {
  checkData: any;
  getAfterChangeData: (data: any) => void;
  getElementXy: (data: any, shouldOpen?: boolean) => void;
  onBeforeOpenKeyPicker: (
    mode: "full" | "cbModifier",
    xy: any,
    shouldOpen?: boolean
  ) => void;
};

const Cb = (props: CbProps) => {
  const { checkData, getAfterChangeData, onBeforeOpenKeyPicker } = props;
  const { t } = useTranslation();
  const [currentKeyRef, setCurrentKeyRef] = useState<any>(null);
  const pickerModeRef = useRef<"full" | "cbModifier">("full");

  const wizardSteps = checkData?.wizard?.steps ?? [];
  const triggerData = checkData?.triggerData ?? [];
  const hasModifierSelected = triggerData[0]?.label || triggerData[1]?.label;
  const isPickerOpen = triggerData.some((t: any) => t.check);

  const setKeyCheck = (key: number, e: any) => {
    const newCheckData = JSON.parse(JSON.stringify(checkData));
    newCheckData.triggerData.forEach((_: any, keys: number) => {
      newCheckData.triggerData[keys].check = keys === key;
    });
    getAfterChangeData(newCheckData);
    setCurrentKeyRef(e.currentTarget);
    const mode: "full" | "cbModifier" = key <= 1 ? "cbModifier" : "full";
    pickerModeRef.current = mode;
    onBeforeOpenKeyPicker(mode, getElementXyData(e));
  };

  useEffect(() => {
    const handleResize = () => {
      if (currentKeyRef) {
        onBeforeOpenKeyPicker(
          pickerModeRef.current,
          getElementXyData({ target: currentKeyRef }),
          false
        );
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [currentKeyRef, onBeforeOpenKeyPicker]);

  return (
    <div className="advance-setting-box">
      <div className="advance-setting-box-header">
        <div className="advance-setting-box-header-title">
          {wizardSteps[1]?.titleKey ? t(wizardSteps[1].titleKey) : ""}
        </div>
        <div className="advance-setting-box-header-desc">
          {wizardSteps[1]?.descKey ? t(wizardSteps[1].descKey) : ""}
        </div>
      </div>
      <div className="operation-item operation-cb">
        <div className="cb-mode-box">
          <Tooltip
            text={
              <div className="pressKey-tooltip">
                <Iconfont icon="warning" className="pressKey-tooltip-icon" />
                <span>
                  {t("keyboard.advancedKey.baseModesText.selectKeyTips2")}
                </span>
              </div>
            }
            trigger="always"
            placement="top"
            usePortal
            open={!isPickerOpen && !hasModifierSelected}
            overlayProps={{
              role: "status",
              "aria-live": "polite",
            }}
          >
            <div className="select-modifier-key">
              {triggerData.slice(0, 2)?.map((item: any, key: number) => {
                const isClick = !item.label && item.check;
                return (
                  <div
                    className="base-pressKey"
                    key={key}
                    onClick={(e: any) => setKeyCheck(key, e)}
                  >
                    <div
                      className={`pressKey ${isClick ? "no-select" : ""} ${item.label ? "isActive" : ""}`}
                    >
                      {item.label ? (
                        <span>
                          {getCbTriggerSlotDisplayLabel(item.keyCode, key) ||
                            item.label}
                        </span>
                      ) : (
                        <Iconfont icon="plus" className="add-icon" />
                      )}
                    </div>
                    <div className="pressKey-desc">
                      {item.label
                        ? t("keyboard.advancedKey.cbModes.hotKey", {
                            num: key + 1,
                          })
                        : t("keyboard.advancedKey.cbModes.selectHotKey", {
                            num: key + 1,
                          })}
                    </div>
                  </div>
                );
              })}
            </div>
          </Tooltip>
          <div className="select-presskey-box">
            {triggerData.slice(2)?.map((item: any, key: number) => {
              const isClick = !item.label && item.check;
              return (
                <Tooltip
                  key={key}
                  text={
                    <div className="pressKey-tooltip">
                      <Iconfont
                        icon="warning"
                        className="pressKey-tooltip-icon"
                      />
                      <span>
                        {t("keyboard.advancedKey.baseModesText.selectKeyTips2")}
                      </span>
                    </div>
                  }
                  trigger="always"
                  placement="top"
                  usePortal
                  open={!isPickerOpen && hasModifierSelected && !item.label}
                  overlayProps={{
                    role: "status",
                    "aria-live": "polite",
                  }}
                >
                  <div
                    className="base-pressKey"
                    onClick={(e: any) => setKeyCheck(2, e)}
                  >
                    <div
                      className={`pressKey ${isClick ? "no-select" : ""} ${item.label ? "isActive" : ""}`}
                    >
                      {item.label ? (
                        <span>
                          {getCbTriggerSlotDisplayLabel(item.keyCode, key) ||
                            item.label}
                        </span>
                      ) : (
                        <Iconfont icon="plus" className="add-icon" />
                      )}
                    </div>
                    <div className="pressKey-desc">
                      {item.label
                        ? t("keyboard.advancedKey.cbModes.regularKey")
                        : t("keyboard.advancedKey.cbModes.needRegularKey")}
                    </div>
                  </div>
                </Tooltip>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cb;
